import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { Users, MessageSquare, Eye, Clock } from 'lucide-react';

export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            api.get('/tracking/stats'),
            api.get('/tracking/sessions'),
        ]).then(([statsRes, sessionsRes]) => {
            setStats(statsRes.data);
            setSessions(sessionsRes.data.slice(0, 5));
        }).catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="text-gray-500">Chargement...</div>;

    const statCards = [
        { label: 'Sessions totales', value: stats?.total_sessions || 0, icon: Eye, color: 'bg-blue-50 text-blue-600' },
        { label: 'Sessions actives', value: stats?.active_sessions || 0, icon: Clock, color: 'bg-green-50 text-green-600' },
        { label: 'Messages', value: stats?.total_messages || 0, icon: MessageSquare, color: 'bg-purple-50 text-purple-600' },
        { label: 'Recruteurs uniques', value: stats?.unique_recruiters_visited || 0, icon: Users, color: 'bg-amber-50 text-amber-600' },
    ];

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {statCards.map(({ label, value, icon: Icon, color }) => (
                    <div key={label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                        <div className="flex items-center gap-3">
                            <div className={`p-2.5 rounded-lg ${color}`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-gray-900">{value}</p>
                                <p className="text-sm text-gray-500">{label}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Recent sessions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Dernières visites</h2>
                </div>
                <div className="divide-y divide-gray-100">
                    {sessions.length === 0 ? (
                        <p className="p-6 text-gray-500 text-center">Aucune session pour le moment</p>
                    ) : (
                        sessions.map((s) => (
                            <div key={s.id} className="px-6 py-4 flex items-center justify-between">
                                <div>
                                    <p className="font-medium text-gray-900">{s.recruiter_name || 'Anonyme'}</p>
                                    <p className="text-sm text-gray-500">{s.company_name || '—'}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm text-gray-500">
                                        {new Date(s.started_at || s.created_at).toLocaleDateString('fr-FR')}
                                    </p>
                                    <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${s.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                        {s.is_active ? 'Active' : 'Terminée'}
                                    </span>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Most asked questions */}
            {stats?.most_asked_questions?.length > 0 && (
                <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Questions fréquentes</h2>
                    </div>
                    <ul className="divide-y divide-gray-100">
                        {stats.most_asked_questions.slice(0, 5).map((q, i) => (
                            <li key={i} className="px-6 py-3 text-sm text-gray-700">"{q}"</li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
